import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DashboardRow {
  readonly dashboardConfigId: string;
  readonly dashboardCode: string;
  readonly dashboardName: string;
  readonly status: string;
  readonly versionNo: number;
}

const STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

/**
 * M40 — Dashboard Builder (§13). The dashboard authoring surface over T529.
 * Compose an operational dashboard of widgets under an application, then
 * publish it. Viewing needs DASHBOARD_VIEW; authoring / publish need
 * DASHBOARD_MANAGE.
 */
export function DashboardBuilder(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [dashboardName, setDashboardName] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', '100');
    apiRequest<{ data: DashboardRow[]; total: number }>(`/v1/app-platform/dashboards?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, statusFilter, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    if (!dashboardName.trim()) { setActionError(t('m40.dashBuilder.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: DashboardRow }>(`/v1/app-platform/dashboards`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), dashboardName: dashboardName.trim() },
        token,
      });
      setNotice(t('m40.common.created', { code: r.data.dashboardCode }));
      setDashboardName('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.dashBuilder.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const publish = async (row: DashboardRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/dashboards/${row.dashboardConfigId}/publish`, { method: 'POST', token });
      setNotice(t('m40.common.published', { code: row.dashboardCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.dashBuilder.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const remove = async (row: DashboardRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/dashboards/${row.dashboardConfigId}`, { method: 'DELETE', token });
      setNotice(t('m40.common.removed', { code: row.dashboardCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.dashBuilder.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="dashboard-builder">
      <h1>{t('m40.dashBuilder.title')}</h1>

      <section aria-label="dashboard-scope">
        <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={setApplicationId} />
      </section>

      <section aria-label="create-dashboard">
        <h2>{t('m40.dashBuilder.newDashboard')}</h2>
        <form aria-label="dashboard-form" onSubmit={(e) => void create(e)}>
          <label>{t('m40.common.name')}<input aria-label="field-dashboard-name" value={dashboardName} onChange={(e) => setDashboardName(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-dashboard" disabled={busy}>{busy ? t('m40.common.creating') : t('m40.dashBuilder.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="dashboard-error">{actionError}</p>}
        {notice && <p aria-label="dashboard-notice">{notice}</p>}
      </section>

      <section aria-label="dashboard-filters">
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('m40.common.allStates')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="dashboard-total">{t('m40.dashBuilder.total', { count: rows.length })}</p>
          <DataTable<DashboardRow>
            ariaLabel="dashboard-table"
            testId="dashboard-table"
            rowKey={(r) => r.dashboardConfigId}
            rowDataAttrs={(r) => ({ 'data-dashboard': r.dashboardCode })}
            emptyMessage={t('m40.dashBuilder.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.dashboardCode },
              { key: 'name', header: t('m40.common.name'), render: (r) => r.dashboardName },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.dashboardCode}`, render: (r) => r.status },
              { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.versionNo}` },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="primary" type="button" aria-label={`publish-${r.dashboardCode}`} onClick={() => void publish(r)}>{t('m40.common.publish')}</Button>
                  <Button variant="danger" type="button" aria-label={`remove-${r.dashboardCode}`} onClick={() => void remove(r)}>{t('m40.common.remove')}</Button>
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
